/**
 * RecordDepositForm — operator-driven deposit recording for a
 * PENDING_DEPOSIT loan.
 *
 * Splits from RecordManualPaymentForm because deposits and installments
 * have different rules:
 *   - Deposit amount is FIXED — must equal `loan.depositAmountMinor`.
 *     The server enforces this (DepositAmountMismatch); the form locks
 *     the amount field so the operator can't accidentally type something
 *     else.
 *   - Deposit happens exactly once per loan (PENDING_DEPOSIT → ACTIVE).
 *     The form only renders for PENDING_DEPOSIT loans.
 *   - Installment payments are free-amount; that flow stays in
 *     RecordManualPaymentForm.
 *
 * Hits the same POST /api/payments/manual endpoint as the installment
 * form. The server's `maybeAdvanceLoanOnDeposit` automatically flips
 * the loan from PENDING_DEPOSIT to ACTIVE when the amount clears.
 *
 * Gating:
 *   - Permission: payment.post_manual (same as installment form). Self-
 *     hides for roles without it; server enforces the same gate.
 *   - Status: PENDING_DEPOSIT only. Hidden otherwise.
 *   - Contract: when loan.contractId is null we DON'T hide this form —
 *     the ContractRequiredBanner above it tells the operator to sign
 *     first; the form's submit attempt would otherwise hit the
 *     loans_contract_required_when_active CHECK and show a server
 *     error, which is less helpful than the banner.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { ManualPaymentChannel, ManualPaymentRequest, SettleResult } from "@dfp/api-client";
import { Button } from "./Button.js";
import { Field, FormError, Select, TextInput, apiErrorMessage } from "./Form.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { generateCashReference, isGeneratedCashReference } from "../lib/payment-reference.js";

interface Props {
  readonly loanId: string;
  readonly loanStatus: string;
  /** Required deposit (server validates equality). Used to prefill + lock the amount field. */
  readonly depositAmountMinor: number;
  readonly depositCurrency: string;
  /**
   * True when the loan still has no contract on file. The deposit can't
   * clear without one (CHECK constraint); the form stays visible but the
   * submit button is disabled with a tooltip directing the operator to
   * the contract-signing banner above.
   */
  readonly contractMissing: boolean;
  /** Called after a successful post so the parent can refresh its loan view. */
  readonly onPosted?: (result: SettleResult) => void;
}

const CHANNEL_LABELS: Record<ManualPaymentChannel, string> = {
  manual_bank_transfer: "Bank transfer",
  manual_cash: "Cash receipt",
  manual_mobile_money_other: "Mobile money (out-of-band)",
  manual_correction: "Correction / suspense",
};

const CHANNEL_OPTIONS: readonly ManualPaymentChannel[] = [
  "manual_bank_transfer",
  "manual_cash",
  "manual_mobile_money_other",
  "manual_correction",
];

function formatMinor(amountMinor: number, currency: string): string {
  // Same whole-vs-sub-unit split as the customer-mobile formatter so the
  // numbers line up across surfaces. TZS/KES/UGX = whole; USD/EUR/GBP/ZAR = sub.
  const isSub = ["USD", "EUR", "GBP", "ZAR"].includes(currency);
  const major = isSub ? amountMinor / 100 : amountMinor;
  const digits = isSub ? 2 : 0;
  const nf = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${currency} ${nf.format(major)}`;
}

export function RecordDepositForm(props: Props): JSX.Element | null {
  const auth = useAuth();
  const canPost = (auth.user?.permissions ?? []).includes("payment.post_manual");
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const [channel, setChannel] = useState<ManualPaymentChannel | "">("");
  const [providerReference, setProviderReference] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [successFlash, setSuccessFlash] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: ManualPaymentRequest) =>
      api.post<SettleResult>("/api/payments/manual", body),
    onSuccess: (result) => {
      reset();
      setOpen(false);
      setSuccessFlash(
        result.idempotent
          ? `Already recorded (${result.payment.providerReference}). No double-charge.`
          : `Deposit posted — ${result.payment.providerReference}` +
              (result.loanAdvanced ? ", loan advanced to ACTIVE." : "."),
      );
      void qc.invalidateQueries({ queryKey: ["loans", "byId", props.loanId] });
      void qc.invalidateQueries({ queryKey: ["loans", "list"] });
      void qc.invalidateQueries({ queryKey: ["payments", "list"] });
      props.onPosted?.(result);
    },
    onError: (err: unknown) => setError(apiErrorMessage(err)),
  });

  function reset(): void {
    setChannel("");
    setProviderReference("");
    setNote("");
    setError(null);
  }

  /**
   * Cash receipts have no external reference, so selecting "Cash receipt"
   * auto-fills a generated `CASH-…` ref (operator can still overtype a
   * physical receipt no.). Switching away from cash clears an auto-generated
   * ref; a hand-typed ref is left untouched.
   */
  function changeChannel(next: ManualPaymentChannel | ""): void {
    setChannel(next);
    setProviderReference((prev) => {
      if (next === "manual_cash") return prev.length === 0 ? generateCashReference() : prev;
      return isGeneratedCashReference(prev) ? "" : prev;
    });
  }

  function submit(): void {
    setError(null);
    if (!channel) {
      setError("Channel is required.");
      return;
    }
    const ref = providerReference.trim();
    if (ref.length < 4) {
      setError("Reference must be at least 4 characters (bank ref / receipt no.).");
      return;
    }
    const trimmedNote = note.trim();
    if (trimmedNote.length > 0 && trimmedNote.length < 3) {
      setError("Note must be at least 3 characters (or leave blank).");
      return;
    }
    mutation.mutate({
      loanId: props.loanId,
      // Locked to the loan's required deposit — server rejects anything else
      // (DepositAmountMismatch). Currency follows the loan, NOT country
      // config, because cross-currency deposit posts would be a different
      // workflow entirely.
      amount: { amountMinor: props.depositAmountMinor, currency: props.depositCurrency },
      channel,
      providerReference: ref,
      ...(trimmedNote.length > 0 ? { note: trimmedNote } : {}),
    });
  }

  // Self-gate: only PENDING_DEPOSIT loans, only operators with the perm.
  if (!canPost) return null;
  if (props.loanStatus !== "PENDING_DEPOSIT") return null;

  if (!open) {
    return (
      <div className="space-y-2">
        {successFlash ? (
          <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-700">
            {successFlash}
          </div>
        ) : null}
        <Button onClick={() => setOpen(true)}>Record deposit</Button>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
      <p className="text-sm font-medium text-slate-700">Record deposit</p>
      <p className="text-xs text-slate-500">
        The deposit amount is fixed at the loan's required deposit. Server rejects any other amount.
        On success the loan flips to ACTIVE.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Deposit (fixed)" hint="locked to the loan's required deposit">
          <TextInput
            value={formatMinor(props.depositAmountMinor, props.depositCurrency)}
            readOnly
            // Make the locked state visually obvious.
            className="bg-slate-100 text-slate-600 cursor-not-allowed"
          />
        </Field>
        <Field label="Channel">
          <Select
            value={channel}
            onChange={(e) => changeChannel(e.target.value as ManualPaymentChannel | "")}
          >
            <option value="">Select channel…</option>
            {CHANNEL_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {CHANNEL_LABELS[c]}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Field
        label="Reference"
        hint={
          channel === "manual_cash"
            ? "auto-generated for cash — or overtype the physical receipt no."
            : "bank ref, cash receipt #, M-Pesa tx id, etc — ≥ 4 chars"
        }
      >
        <TextInput
          value={providerReference}
          onChange={(e) => setProviderReference(e.target.value)}
        />
      </Field>

      <Field label="Note" hint="optional — why are you posting manually? lands on the audit row">
        <TextInput value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>

      {props.contractMissing ? (
        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          Contract is not signed yet. The deposit will be rejected by the server until the contract
          is in place — use the banner above to sign first.
        </div>
      ) : null}

      <FormError message={error} />

      <div className="flex gap-2">
        <Button onClick={submit} disabled={mutation.isPending || props.contractMissing}>
          {mutation.isPending ? "Posting…" : "Post deposit"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
